import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { motion } from "framer-motion";
import { CheckCircle, Clock, Ticket } from "@phosphor-icons/react";
import { API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { tierFor } from "../../lib/tiers";

export default function Pass() {
  const { user } = useAuth();
  const [token, setToken] = useState(null);
  const [countdown, setCountdown] = useState(30);
  const [passes, setPasses] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [rewards, setRewards] = useState(null);

  useEffect(() => {
    const fetchToken = () => {
      API.get("/pass/token").then((r) => { setToken(r.data.token); setCountdown(30); }).catch(() => {});
    };
    fetchToken();
    const t = setInterval(fetchToken, 30000);
    const c = setInterval(() => setCountdown((s) => Math.max(0, s - 1)), 1000);
    API.get("/pass/tonight").then((r) => setPasses(r.data)).catch(() => {});
    API.get("/me/checkins").then((r) => setCheckins(r.data.slice(0, 4))).catch(() => {});
    API.get("/me/rewards").then((r) => setRewards(r.data)).catch(() => {});
    return () => { clearInterval(t); clearInterval(c); };
  }, []);

  const tier = rewards ? rewards.tier : tierFor(user?.nite_points || 0).tier;

  return (
    <div data-testid="pass-page" className="px-5 pt-8">
      <div className="text-center">
        <div className="neon-overline">Your NITEPASS</div>
        <h1 className="mt-1 font-display text-2xl font-black">{user?.name}</h1>
        <div className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-neon-yellow">{tier} · {rewards?.points ?? user?.nite_points ?? 0} NITE POINTS</div>
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="relative mx-auto mt-6 w-fit">
        <div className="absolute -inset-4 rounded-[28px] bg-neon-gold/25 blur-2xl" />
        <div className="relative rounded-[28px] border border-neon-gold/50 bg-white p-5 shadow-glow-gold" data-testid="qr-pass">
          {token ? (
            <QRCodeSVG value={token} size={228} bgColor="#ffffff" fgColor="#050505" level="M" includeMargin={false} data-testid="qr-code" />
          ) : (
            <div className="flex h-[228px] w-[228px] items-center justify-center text-sm text-zinc-400">Loading pass...</div>
          )}
        </div>
      </motion.div>
      <div className="mx-auto mt-4 max-w-[260px]">
        <div className="h-1 overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-neon-violet transition-[width] duration-1000 ease-linear" style={{ width: `${(countdown / 30) * 100}%` }} />
        </div>
        <p className="mt-2 text-center text-[11px] text-zinc-500">Dynamic code refreshes in {countdown}s — screenshots won't work at the door.</p>
      </div>

      <section className="mt-8">
        <h2 className="mb-3 flex items-center gap-2 font-display text-base font-bold"><Ticket size={17} className="text-neon-gold" /> TONIGHT'S PASSES</h2>
        {passes.length === 0 && (
          <div className="card-neon p-5 text-center text-sm text-zinc-500" data-testid="no-passes">
            No guest-list passes yet. Guest lists are invite-only — a venue or promoter can add you, or just scan your pass at the door.
          </div>
        )}
        <div className="space-y-3">
          {passes.map((p) => (
            <div key={p.id} data-testid={`pass-${p.id}`} className="card-neon border-neon-violet/30 p-4">
              <div className="neon-overline !text-neon-violet">You're on the list</div>
              <div className="mt-1 font-display text-lg font-black">{p.venue_name}</div>
              <div className="text-sm text-zinc-300">{p.event?.title} · {p.event?.date_label} {p.event?.start}</div>
              <div className="mt-1 text-xs text-zinc-500">Hosted by {p.promoter_handle || "the venue"}</div>
              <div className="mt-2 inline-block rounded-full bg-neon-green/10 px-3 py-1 text-[11px] font-bold text-neon-green">{p.perk}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="mb-3 flex items-center gap-2 font-display text-base font-bold"><Clock size={17} className="text-neon-violet" /> PAST CHECK-INS</h2>
        <div className="space-y-2" data-testid="past-checkins">
          {checkins.map((c) => (
            <div key={c.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
              <div>
                <div className="text-sm font-bold">{c.venue_name}</div>
                <div className="text-[11px] text-zinc-500">{c.date} · {c.entry_point}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold text-neon-yellow">+{c.points}</div>
                {c.reward_unlocked && <div className="flex items-center gap-1 text-[10px] font-bold text-neon-green"><CheckCircle size={11} weight="fill" />{c.reward_unlocked}</div>}
              </div>
            </div>
          ))}
          {checkins.length === 0 && <p className="py-4 text-center text-sm text-zinc-500">No check-ins yet. Tonight's the night.</p>}
        </div>
      </section>
    </div>
  );
}
